import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { isSuperAdmin } from '@/utils/auth';
import { ENABLED_GAME_TYPES } from '@/lib/platform-filters';
import { useMemo } from 'react';
import {
  useUserDetailQuery,
  type UserSelectionRow,
  type UserDailyRevealRow,
  type PayoutRow,
  type PickemLeaderboardRow,
} from '@/api/users/getUsers';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User as UserIcon } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { buildPayoutBreakdown, type PayoutBreakdown } from '@/utils/payouts';

type ActivityFilter = 'all' | 'pickem' | 'squares' | 'daily_reveal';

const VALID_FILTERS: ActivityFilter[] = ['all', 'pickem', 'squares', 'daily_reveal'];

export const Route = createFileRoute('/dashboard/users/$id')({
  beforeLoad: () => {
    if (!isSuperAdmin()) {
      throw redirect({ to: '/dashboard' });
    }
  },
  validateSearch: (search: Record<string, unknown>): { game?: ActivityFilter } => {
    const game = typeof search.game === 'string' ? (search.game as ActivityFilter) : undefined;
    return { game: game && VALID_FILTERS.includes(game) ? game : undefined };
  },
  component: UserDetailPage,
});

interface PickemGroup {
  pickemId: string;
  pickemTitle: string;
  sport: string | null;
  gameType: string | null;
  pickemStatus: string | null;
  pickemStartUtc: string | null;
  selections: UserSelectionRow[];
  totalPoints: number;
  squaresPrize: number; // Sum of prize_amounts_by_period for squares contests
}

interface DailyRevealGroup {
  pickemId: string;
  pickemTitle: string;
  entries: UserDailyRevealRow[];
}

function sumPeriodPrizes(periods: Record<string, string | number> | null | undefined): number {
  if (!periods) return 0;
  let sum = 0;
  for (const v of Object.values(periods)) {
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) sum += n;
  }
  return sum;
}

function PayoutBadges({ breakdown }: { breakdown: PayoutBreakdown }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {breakdown.cash > 0 && <Badge>{`£${breakdown.cash.toFixed(2)} cash`}</Badge>}
      {breakdown.bonusBet > 0 && (
        <Badge variant="secondary">{`£${breakdown.bonusBet.toFixed(2)} bonus bet`}</Badge>
      )}
      {breakdown.crowns > 0 && (
        <Badge variant="secondary">{`${breakdown.crowns.toLocaleString()} crowns`}</Badge>
      )}
      {breakdown.profitBoosts.map((pct, i) => (
        <Badge key={`pb-${i}`} variant="outline">{`+${pct}% boost`}</Badge>
      ))}
      {breakdown.others.map((desc, i) => (
        <Badge key={`other-${i}`} variant="outline">
          {desc}
        </Badge>
      ))}
      {breakdown.pendingCash > 0 && (
        <Badge
          variant="outline"
          className="text-muted-foreground"
        >{`£${breakdown.pendingCash.toFixed(2)} pending`}</Badge>
      )}
    </div>
  );
}

function UserDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const filter: ActivityFilter = search.game ?? 'all';
  const { data, isLoading, error } = useUserDetailQuery(id);

  const setFilter = (next: ActivityFilter) => {
    navigate({
      to: '/dashboard/users/$id',
      params: { id },
      search: { game: next === 'all' ? undefined : next },
    });
  };

  const grouped = useMemo<PickemGroup[]>(() => {
    if (!data) return [];
    const map = new Map<string, PickemGroup>();
    for (const sel of data.selections) {
      const key = sel.pickemId || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          pickemId: key,
          pickemTitle: sel.pickemTitle || 'Untitled',
          sport: sel.sport,
          gameType: sel.gameType,
          pickemStatus: sel.pickemStatus,
          pickemStartUtc: sel.pickemStartUtc,
          selections: [],
          totalPoints: 0,
          squaresPrize: 0,
        });
      }
      const g = map.get(key)!;
      g.selections.push(sel);
      g.totalPoints += sel.pointsEarned ?? 0;
      g.squaresPrize += sumPeriodPrizes(sel.prizeAmountsByPeriod);
    }
    return Array.from(map.values()).sort((a, b) => {
      const aTime = a.pickemStartUtc ? new Date(a.pickemStartUtc).getTime() : 0;
      const bTime = b.pickemStartUtc ? new Date(b.pickemStartUtc).getTime() : 0;
      return bTime - aTime;
    });
  }, [data]);

  const pickemGroups = useMemo(
    () => grouped.filter(g => (g.gameType || '').toLowerCase() === 'pickem'),
    [grouped]
  );
  const squaresGroups = useMemo(
    () => grouped.filter(g => (g.gameType || '').toLowerCase() === 'squares'),
    [grouped]
  );

  const drGroups = useMemo<DailyRevealGroup[]>(() => {
    if (!data) return [];
    const map = new Map<string, DailyRevealGroup>();
    for (const e of data.dailyRevealEntries) {
      const key = e.pickemId || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          pickemId: key,
          pickemTitle: e.pickemTitle || 'Untitled',
          entries: [],
        });
      }
      map.get(key)!.entries.push(e);
    }
    return Array.from(map.values());
  }, [data]);

  const payoutsByPickem = useMemo(() => {
    const m = new Map<string, PayoutBreakdown>();
    if (data) {
      const grouped = new Map<string, PayoutRow[]>();
      for (const p of data.payouts) {
        const k = p.pickemId || 'unknown';
        const arr = grouped.get(k) ?? [];
        arr.push(p);
        grouped.set(k, arr);
      }
      for (const [k, rows] of grouped) m.set(k, buildPayoutBreakdown(rows));
    }
    return m;
  }, [data]);

  const leaderboardByPickem = useMemo(() => {
    const m = new Map<string, PickemLeaderboardRow>();
    if (data) for (const lb of data.pickemLeaderboards) m.set(lb.pickemId, lb);
    return m;
  }, [data]);

  if (isLoading) {
    return (
      <PageContainer>
        <div className="text-muted-foreground py-8 text-center">Loading user…</div>
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer>
        <div className="text-red-600 py-8 text-center">Failed to load user.</div>
      </PageContainer>
    );
  }

  const u = data.user;
  const pickemPickCount = pickemGroups.reduce((n, g) => n + g.selections.length, 0);
  const squaresPickCount = squaresGroups.reduce((n, g) => n + g.selections.length, 0);
  const drDayCount = data.dailyRevealEntries.length;
  const drContestCount = drGroups.length;
  const totalCash = data.payouts.reduce((sum, p) => {
    if (p.prizeType === 'cash' && p.paymentStatus === 'paid' && p.prizeAmount != null) {
      return sum + Number(p.prizeAmount);
    }
    return sum;
  }, 0);
  const totalBonusBet = data.payouts.reduce((sum, p) => {
    if (p.prizeType === 'bonus_bet' && p.paymentStatus === 'paid' && p.prizeAmount != null) {
      return sum + Number(p.prizeAmount);
    }
    return sum;
  }, 0);
  const totalCrowns = data.payouts.reduce((sum, p) => {
    if (p.prizeType === 'crowns' && p.paymentStatus === 'paid' && p.prizeAmount != null) {
      return sum + Number(p.prizeAmount);
    }
    return sum;
  }, 0);

  // Per-game tab badge counts, keyed by sports-config game type code.
  const gameTypeTabCounts: Record<string, number> = {
    pickem: pickemGroups.length,
    squares: squaresGroups.length,
    daily_reveal: drContestCount,
  };

  return (
    <PageContainer>
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate({ to: '/dashboard/users' })}
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to users
      </Button>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            {u.username || u.ssoUserId || u.id}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Field label="User ID" value={u.id} mono />
            <Field label="SSO ID" value={u.ssoUserId} mono />
            <Field label="Username" value={u.username} />
            <Field label="Location" value={u.location} />
            <div>
              <div className="text-muted-foreground text-xs">Signed up</div>
              <div>
                <TzDateDisplay utcDate={u.createdAt} showUtcBadge={false} />
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4 flex-wrap">
            <Badge variant="secondary">
              {pickemGroups.length.toLocaleString()} Pick&apos;em contests
              {pickemPickCount > 0 && ` · ${pickemPickCount.toLocaleString()} picks`}
            </Badge>
            <Badge variant="secondary">
              {squaresGroups.length.toLocaleString()} Squares contests
              {squaresPickCount > 0 && ` · ${squaresPickCount.toLocaleString()} squares`}
            </Badge>
            <Badge variant="secondary">
              {drContestCount.toLocaleString()} Daily Reveal contests
              {drDayCount > 0 && ` · ${drDayCount.toLocaleString()} days played`}
            </Badge>
            {totalCash > 0 && <Badge>{`£${totalCash.toFixed(2)} cash`}</Badge>}
            {totalBonusBet > 0 && (
              <Badge variant="secondary">{`£${totalBonusBet.toFixed(2)} bonus bet`}</Badge>
            )}
            {totalCrowns > 0 && (
              <Badge variant="secondary">{`${totalCrowns.toLocaleString()} crowns`}</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={filter} onValueChange={v => setFilter(v as ActivityFilter)} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          {/* One TabsTrigger per game this client deployed (Layer A) — adding a
              game type only requires updating the registry, not this file. */}
          {ENABLED_GAME_TYPES.map(gameType => {
            const count = gameTypeTabCounts[gameType.code] ?? 0;
            return (
              <TabsTrigger key={gameType.code} value={gameType.code}>
                {gameType.label}
                {count > 0 ? ` (${count})` : ''}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Pick'em activity */}
      {(filter === 'all' || filter === 'pickem') && pickemGroups.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Pick&apos;em activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {pickemGroups.map(group => (
              <ContestActivityBlock
                key={group.pickemId}
                group={group}
                kind="pickem"
                breakdown={payoutsByPickem.get(group.pickemId)}
                leaderboard={leaderboardByPickem.get(group.pickemId)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Squares activity */}
      {(filter === 'all' || filter === 'squares') && squaresGroups.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Squares activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {squaresGroups.map(group => (
              <ContestActivityBlock
                key={group.pickemId}
                group={group}
                kind="squares"
                breakdown={payoutsByPickem.get(group.pickemId)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Daily Reveal activity */}
      {(filter === 'all' || filter === 'daily_reveal') && drGroups.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Daily Reveal activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {drGroups.map(group => {
              const breakdown = payoutsByPickem.get(group.pickemId);
              const totalHits = group.entries.reduce((n, e) => n + (e.totalHits ?? 0), 0);
              return (
                <div key={group.pickemId} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <div className="font-medium">{group.pickemTitle}</div>
                      <div className="text-xs text-muted-foreground">
                        {group.entries.length} day{group.entries.length === 1 ? '' : 's'} played ·{' '}
                        {totalHits} total hit{totalHits === 1 ? '' : 's'}
                      </div>
                    </div>
                    {breakdown && <PayoutBadges breakdown={breakdown} />}
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Day</TableHead>
                        <TableHead>Game date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Hits</TableHead>
                        <TableHead>Entered at</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.entries.map(e => (
                        <TableRow key={e.id}>
                          <TableCell>{e.dayNumber != null ? `Day ${e.dayNumber}` : '—'}</TableCell>
                          <TableCell className="text-sm">
                            {e.gameDate ? (
                              <TzDateDisplay
                                utcDate={e.gameDate}
                                formatString="MMM d, yyyy"
                                showUtcBadge={false}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>{e.status || '—'}</TableCell>
                          <TableCell className="text-right font-bold">{e.totalHits ?? 0}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <TzDateDisplay utcDate={e.createdAt} showUtcBadge={false} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {(() => {
        const visibleCount =
          (filter === 'all' || filter === 'pickem' ? pickemGroups.length : 0) +
          (filter === 'all' || filter === 'squares' ? squaresGroups.length : 0) +
          (filter === 'all' || filter === 'daily_reveal' ? drGroups.length : 0);
        if (visibleCount > 0) return null;
        const label =
          filter === 'pickem'
            ? "Pick'em"
            : filter === 'squares'
              ? 'Squares'
              : filter === 'daily_reveal'
                ? 'Daily Reveal'
                : null;
        return (
          <div className="text-center text-muted-foreground py-8">
            {label
              ? `No ${label} activity for this user.`
              : "This user hasn't entered any contests yet."}
          </div>
        );
      })()}
    </PageContainer>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className={mono ? 'font-mono text-xs break-all' : ''}>{value || '—'}</div>
    </div>
  );
}

function ContestActivityBlock({
  group,
  kind,
  breakdown,
  leaderboard,
}: {
  group: PickemGroup;
  kind: 'pickem' | 'squares';
  breakdown?: PayoutBreakdown;
  leaderboard?: PickemLeaderboardRow;
}) {
  const rank = leaderboard?.rank ?? breakdown?.rank ?? null;

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-medium">{group.pickemTitle}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            {group.sport && <span>{group.sport}</span>}
            {group.pickemStatus && <span>· {group.pickemStatus}</span>}
            {group.pickemStartUtc && (
              <span>
                ·{' '}
                <TzDateDisplay
                  utcDate={group.pickemStartUtc}
                  formatString="MMM d, yyyy"
                  showUtcBadge={false}
                />
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {kind === 'pickem' && rank != null && <Badge variant="outline">{`Rank #${rank}`}</Badge>}
          {kind === 'pickem' && (
            // Sum the per-question points_earned shown in the rows below rather than the
            // stored pickem_leaderboards.points: that denormalized total is only refreshed
            // by PickemScoringService re-scores and goes stale during live grading, so it
            // can disagree with the live-graded per-question points the admin sees here.
            <Badge variant="outline">{`${group.totalPoints} pts`}</Badge>
          )}
          {breakdown && <PayoutBadges breakdown={breakdown} />}
        </div>
      </div>

      {kind === 'pickem' ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Question</TableHead>
              <TableHead>Pick</TableHead>
              <TableHead className="text-right">Points</TableHead>
              <TableHead>Picked at</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.selections.map(sel => (
              <TableRow key={sel.id}>
                <TableCell className="text-sm">{sel.questionTitle || '—'}</TableCell>
                <TableCell className="text-sm">
                  {sel.optionTitle || sel.customValue || '—'}
                </TableCell>
                <TableCell className="text-right">{sel.pointsEarned ?? 0}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  <TzDateDisplay utcDate={sel.createdAt} showUtcBadge={false} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Square</TableHead>
              <TableHead className="text-right">Prize won</TableHead>
              <TableHead>Entered at</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {group.selections.map(sel => {
              const periodPrize = sumPeriodPrizes(sel.prizeAmountsByPeriod);
              const periods = sel.prizeAmountsByPeriod
                ? Object.entries(sel.prizeAmountsByPeriod).filter(([, v]) => Number(v) > 0)
                : [];
              return (
                <TableRow key={sel.id}>
                  <TableCell className="text-sm">
                    {sel.optionTitle || sel.customValue || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {periodPrize > 0 ? (
                      <div>
                        <div className="font-medium">£{periodPrize.toFixed(2)}</div>
                        {periods.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {periods.map(([k, v]) => `${k}: £${Number(v).toFixed(2)}`).join(', ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <TzDateDisplay utcDate={sel.createdAt} showUtcBadge={false} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
